/**
 * The to-do tree, worked out from the flat list the server sends.
 *
 * Gordon: "allow parent - son - grandson relationship". Three levels and no
 * more: a root (0), its children (1) and their children (2). The server is
 * the judge of every move; these functions only decide what to OFFER, so a
 * drop the server would refuse never shows an indicator in the first place.
 */
export const MAX_LEVEL = 2;

// A PINNED to-do (batch 5) floats to the top of its siblings; the rest keep
// their own order. The server's order is by position alone, so a move is
// sent as a place among its neighbours there (components/todos/notes.js).
const order = (a, b) => ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))
  || (Number(a.position ?? 0) - Number(b.position ?? 0))
  || String(a.created_at || '').localeCompare(String(b.created_at || ''));

export function buildTree(open) {
  const byId = new Map(open.map((t) => [t.id, t]));
  // A row whose parent is not in the open list is shown as a root rather
  // than lost - the contract says it cannot happen, but it costs nothing.
  const parentOf = (id) => {
    const t = byId.get(id);
    return t && t.parent_id && byId.has(t.parent_id) ? t.parent_id : null;
  };
  const kids = new Map();
  for (const t of open) {
    const p = parentOf(t.id) || '';
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(t);
  }
  for (const list of kids.values()) list.sort(order);
  const childrenOf = (id) => kids.get(id || '') || [];
  const levelOf = (id) => {
    let l = 0;
    for (let p = parentOf(id); p && l < 10; p = parentOf(p)) l += 1;
    return l;
  };
  const heightOf = (id) => {
    const c = childrenOf(id);
    return c.length ? 1 + Math.max(...c.map((x) => heightOf(x.id))) : 0;
  };
  const descendantsOf = (id) => childrenOf(id).flatMap((c) => [c.id, ...descendantsOf(c.id)]);
  const others = (parentId, id) => childrenOf(parentId).filter((t) => t.id !== id);
  return { byId, parentOf, childrenOf, levelOf, heightOf, descendantsOf, others };
}

/** The rows on screen, in order: a collapsed parent hides everything under it. */
export function visibleRows(tree, collapsed) {
  const rows = [];
  const walk = (parentId, level) => {
    tree.childrenOf(parentId).forEach((item, index, sibs) => {
      const hasKids = tree.childrenOf(item.id).length > 0;
      const isCollapsed = hasKids && collapsed.has(item.id);
      rows.push({ item, level, index, count: sibs.length, parentId,
                  prev: sibs[index - 1] || null, hasKids, collapsed: isCollapsed });
      if (hasKids && !isCollapsed) walk(item.id, level + 1);
    });
  };
  walk(null, 0);
  return rows;
}

/**
 * Dropping `dragId` into the gap after rows[a] (a = -1: the very top) at
 * depth `want`. `rows` are the visible rows WITHOUT the dragged subtree, so
 * "into its own descendant" is not even on the table. The row below the gap
 * sets the shallowest depth allowed (you cannot cut a parent off from its
 * next child), the row above the deepest (one under it), and the dragged
 * subtree's own height caps it at three levels. Returns
 * { parentId, index, depth } or null.
 *
 * `height` is the dragged subtree's height when it comes from ANOTHER tree -
 * a section of a list is a tree of its own, and an item dragged in from the
 * next section is not in this one to be measured.
 */
export function gapTarget(tree, rows, dragId, a, want, height = tree.heightOf(dragId)) {
  const above = a >= 0 ? rows[a] : null;
  const below = rows[a + 1] || null;
  const max = Math.min(above ? above.level + 1 : 0, MAX_LEVEL - height);
  const min = below ? below.level : 0;
  if (min > max) return null;
  const depth = Math.max(min, Math.min(max, want));
  if (!above) return { parentId: null, index: 0, depth };
  if (depth === above.level + 1) {
    const sibs = tree.others(above.item.id, dragId);
    return { parentId: above.item.id, index: above.collapsed ? sibs.length : 0, depth };
  }
  let anc = above.item.id;
  for (let l = above.level; l > depth; l -= 1) anc = tree.parentOf(anc);
  const parentId = tree.parentOf(anc);
  const sibs = tree.others(parentId, dragId);
  return { parentId, index: sibs.findIndex((t) => t.id === anc) + 1, depth };
}

/** Dropping `dragId` onto a row: it becomes a sub-task of that row. */
export function nestTarget(tree, row, dragId, height = tree.heightOf(dragId)) {
  if (row.level + 1 + height > MAX_LEVEL) return null;
  const sibs = tree.others(row.item.id, dragId);
  return { parentId: row.item.id,
           index: row.collapsed || !sibs.length ? sibs.length : 0,
           depth: row.level + 1 };
}

/** Would this target leave the item exactly where it is? */
export function isNoop(tree, dragId, target) {
  if (tree.parentOf(dragId) !== (target.parentId || null)) return false;
  const here = tree.childrenOf(target.parentId).findIndex((t) => t.id === dragId);
  return here === target.index;
}

/**
 * The open list as it will be after the move - shown while the server answers.
 *
 * `scope` ({ list_id, section_id }), when given, is where the item is going:
 * it and everything under it take that list and section, and its new
 * siblings are counted among that list and section only - "index 2 among the
 * roots" means the roots of one section, not of everything the person owns.
 */
export function applyMove(open, dragId, parentId, index, scope = null) {
  let all = open;
  if (scope) {
    const moving = new Set([dragId, ...buildTree(open).descendantsOf(dragId)]);
    all = open.map((t) => (moving.has(t.id)
      ? { ...t, list_id: scope.list_id, section_id: scope.section_id ?? null } : t));
  }
  const inScope = scope
    ? all.filter((t) => t.list_id === scope.list_id && (t.section_id ?? null) === (scope.section_id ?? null))
    : all;
  const tree = buildTree(inScope);
  const sibs = tree.others(parentId, dragId);
  sibs.splice(index, 0, tree.byId.get(dragId));
  const pos = new Map(sibs.map((t, i) => [t.id, i]));
  return all.map((t) => (pos.has(t.id)
    ? { ...t, position: pos.get(t.id), parent_id: t.id === dragId ? parentId : t.parent_id }
    : t));
}

/** How many rows of `list` sit under `id`, at any depth. */
export function countUnder(list, id) {
  let n = 0;
  const walk = (pid) => list.forEach((t) => {
    if (t.parent_id === pid) { n += 1; walk(t.id); }
  });
  walk(id);
  return n;
}
